import { educationRequest } from './index'

const BASE = '/ai-education/university'

// ---------- Term ----------
export interface TermVO {
  id?: number
  schoolId: number
  schoolName?: string
  name: string
  code: string
  startDate: string
  endDate: string
  enrollStartTime?: string
  enrollEndTime?: string
  weekCount?: number
  examStartDate?: string
  examEndDate?: string
  maxCredit?: number
  status?: number
  remark?: string
  createTime?: string
}

export interface TermSimpleVO {
  id: number
  schoolId: number
  name: string
  code: string
  status?: number
}

export const saveTerm = (data: Partial<TermVO>) =>
  educationRequest.post<number>({ url: `${BASE}/term/save`, data })
export const getTermList = (schoolId?: number) =>
  educationRequest.get<TermVO[]>({ url: `${BASE}/term/list`, params: { schoolId } })
export const getTermSimpleList = (schoolId?: number) =>
  educationRequest.get<TermSimpleVO[]>({
    url: `${BASE}/term/list-simple`,
    params: { schoolId },
    headers: { ignoreError: true }
  })
export const getTerm = (id: number) =>
  educationRequest.get<TermVO>({ url: `${BASE}/term/get`, params: { id } })
export const deleteTerm = (id: number) =>
  educationRequest.delete<boolean>({ url: `${BASE}/term/delete`, params: { id } })

// ---------- Enrollment ----------
export interface EnrollmentVO {
  id: number
  schoolId: number
  schoolName?: string
  termId: number
  termName?: string
  teachingClassId: number
  teachingClassName?: string
  studentId: number
  studentName?: string
  status: number
  waitlistNo?: number
  enrollTime?: string
  confirmTime?: string
  dropTime?: string
  operatorId?: number
  remark?: string
  createTime?: string
}

export const getEnrollmentList = (params?: {
  teachingClassId?: number
  termId?: number
  status?: number
}) => educationRequest.get<EnrollmentVO[]>({ url: `${BASE}/enrollment/list`, params })
export const promoteWaitlist = (teachingClassId: number) =>
  educationRequest.post<number>({
    url: `${BASE}/enrollment/promote`,
    params: { teachingClassId }
  })
export const forceEnroll = (data: { teachingClassId: number; studentId: number; remark?: string }) =>
  educationRequest.post<number>({ url: `${BASE}/enrollment/force-enroll`, data })
export const dropEnrollment = (enrollmentId: number) =>
  educationRequest.post<boolean>({
    url: `${BASE}/enrollment/drop`,
    params: { enrollmentId }
  })

// ---------- Timetable ----------
export interface TimetableVO {
  id?: number
  schoolId?: number
  termId?: number
  teachingClassId: number
  teachingClassName?: string
  teacherId?: number
  weekday: number
  periodStart: number
  periodEnd: number
  weekStart?: number
  weekEnd?: number
  roomText?: string
  status?: number
  createTime?: string
}

export interface TimetableConflictCheckReq {
  id?: number
  teachingClassId: number
  weekday: number
  periodStart: number
  periodEnd: number
  weekStart?: number
  weekEnd?: number
}

export const saveTimetable = (data: Partial<TimetableVO>) =>
  educationRequest.post<number>({ url: `${BASE}/timetable/save`, data })
export const getTimetableList = (params?: { teachingClassId?: number; termId?: number }) =>
  educationRequest.get<TimetableVO[]>({ url: `${BASE}/timetable/list`, params })
export const deleteTimetable = (id: number) =>
  educationRequest.delete<boolean>({ url: `${BASE}/timetable/delete`, params: { id } })
export const conflictCheckTimetable = (data: TimetableConflictCheckReq) =>
  educationRequest.post<TimetableVO[]>({ url: `${BASE}/timetable/conflict-check`, data })

// ---------- Attendance ----------
export interface AttendanceSessionVO {
  id?: number
  schoolId?: number
  teachingClassId: number
  teachingClassName?: string
  title: string
  sessionNo?: number
  startTime: string
  endTime: string
  checkinDeadline?: string
  status?: number
  liveSessionId?: number
  createTime?: string
}

export interface AttendanceRecordVO {
  id: number
  sessionId: number
  teachingClassId: number
  studentId: number
  studentName?: string
  result: number
  checkinTime?: string
  source?: number
  operatorId?: number
  remark?: string
  createTime?: string
}

export const saveAttendanceSession = (data: Partial<AttendanceSessionVO>) =>
  educationRequest.post<number>({ url: `${BASE}/attendance/session/save`, data })
export const getAttendanceSessionList = (teachingClassId: number) =>
  educationRequest.get<AttendanceSessionVO[]>({
    url: `${BASE}/attendance/session/list`,
    params: { teachingClassId }
  })
export const openAttendanceCheckin = (sessionId: number) =>
  educationRequest.put<boolean>({
    url: `${BASE}/attendance/session/open`,
    params: { sessionId }
  })
export const getAttendanceRecords = (sessionId: number) =>
  educationRequest.get<AttendanceRecordVO[]>({
    url: `${BASE}/attendance/session/records`,
    params: { sessionId }
  })
export const makeupAttendance = (data: {
  sessionId: number
  studentId: number
  result: number
  remark?: string
}) => educationRequest.post<number>({ url: `${BASE}/attendance/session/makeup`, data })

// ---------- Teach session ----------
export interface TeachSessionVO {
  id?: number
  schoolId?: number
  teachingClassId: number
  teachingClassName?: string
  title: string
  liveRoomId: number
  scheduledStartTime?: string
  scheduledEndTime?: string
  status?: number
  remark?: string
  createTime?: string
}

export const saveTeachSession = (data: Partial<TeachSessionVO>) =>
  educationRequest.post<number>({ url: `${BASE}/teach-session/save`, data })
export const getTeachSessionList = (teachingClassId: number) =>
  educationRequest.get<TeachSessionVO[]>({
    url: `${BASE}/teach-session/list`,
    params: { teachingClassId }
  })
export const deleteTeachSession = (id: number) =>
  educationRequest.delete<boolean>({ url: `${BASE}/teach-session/delete`, params: { id } })

// ---------- Gradebook ----------
export interface GradebookVO {
  id: number
  schoolId: number
  schoolName?: string
  termId?: number
  termName?: string
  teachingClassId: number
  teachingClassName?: string
  title?: string
  status: number
  usualWeight?: number
  homeworkWeight?: number
  examWeight?: number
  submitUserId?: number
  submitTime?: string
  firstReviewUserId?: number
  firstReviewTime?: string
  finalReviewUserId?: number
  finalReviewTime?: string
  publishTime?: string
  rejectReason?: string
  createTime?: string
}

export interface GradeScoreVO {
  id?: number
  gradebookId: number
  teachingClassId?: number
  studentId: number
  usualScore?: number
  homeworkScore?: number
  examScore?: number
  totalScore?: number
  absentExam?: boolean
  remark?: string
  createTime?: string
}

export const getOrCreateGradebook = (data: {
  teachingClassId: number
  title?: string
  usualWeight?: number
  homeworkWeight?: number
  examWeight?: number
}) => educationRequest.post<GradebookVO>({ url: `${BASE}/gradebook/get-or-create`, data })
export const saveGradeScores = (data: {
  gradebookId: number
  scores: Array<{
    studentId: number
    usualScore?: number
    homeworkScore?: number
    examScore?: number
    absentExam?: boolean
    remark?: string
  }>
}) => educationRequest.post<boolean>({ url: `${BASE}/gradebook/save-scores`, data })
export const submitGradebook = (gradebookId: number) =>
  educationRequest.put<boolean>({ url: `${BASE}/gradebook/submit`, params: { gradebookId } })
export const firstReviewGradebook = (gradebookId: number) =>
  educationRequest.put<boolean>({
    url: `${BASE}/gradebook/first-review`,
    params: { gradebookId }
  })
export const finalReviewGradebook = (gradebookId: number) =>
  educationRequest.put<boolean>({
    url: `${BASE}/gradebook/final-review`,
    params: { gradebookId }
  })
export const rejectGradebook = (data: { gradebookId: number; rejectReason?: string }) =>
  educationRequest.post<boolean>({ url: `${BASE}/gradebook/reject`, data })
export const getGradeScores = (gradebookId: number) =>
  educationRequest.get<GradeScoreVO[]>({ url: `${BASE}/gradebook/scores`, params: { gradebookId } })
